<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white">
          <v-toolbar-title class="title">{{ $t("Flow Management") }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>
      <v-card class="card2">
        <v-card-text>
          <v-row align="center">
            <!-- ส่วนค้นหา -->
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="pa-2 search-panel">
                <v-row no-gutters style="display: flex;align-items: center;gap: 18px;padding: 10px;">
                  <v-text-field
                    id="search-flowname-flowlist"
                    label="Flow Name"
                    density="comfortable"
                    variant="outlined"
                    prepend-inner-icon="mdi-text-box-search-outline"
                    hide-details
                    clearable
                    v-model="searchFlowName"
                    @keydown.enter="Search"
                    color="primary"
                  ></v-text-field>
                  <v-btn
                    id="button-search-flowlist"
                    color="primary"
                    class="ml-2"
                    variant="elevated"
                    style="min-width: 100px;"
                    @click="Search"
                  >
                    <v-icon start>mdi-magnify</v-icon>
                    Search
                  </v-btn>
                </v-row>
              </v-card>
            </v-col>

            <!-- ส่วนปุ่มฝารทำงาน -->
            <v-col cols="12" md="6" class="d-flex flex-wrap justify-end gap-2">
              <div>
                
                <!-- Divider -->
                <v-divider vertical thickness="1" length="50" class="mx-4"></v-divider>
                
                <!-- Create Button -->
                <v-btn
                  v-if="canAdd"
                  id="button-create-flowlist"
                  color="primary"
                  class="control-btn create-btn"
                  variant="flat"
                  size="large"
                  prepend-icon="mdi-plus-circle"
                  @click="createFlow"
                  rounded="lg"
                >
                  CREATE
                  <v-tooltip activator="parent" location="top">
                    Create new Flow
                  </v-tooltip>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-data-table-server
          class="modern-table"
          v-model:options="options"
          :items-per-page="itemsPerPage"
          :items-length="searchTotalTable"
          :items="flows"
          :headers="headers"
          :loading="loading"
        >
          <!-- No Data Template -->
          <template v-slot:no-data>
            <div class="no-data-container">
              <div class="no-data-content">
                <!-- Icon -->
                <v-icon size="64" color="grey-lighten-2" class="no-data-icon">
                  mdi-chart-timeline-variant-shimmer
                </v-icon>
                
                <!-- Message -->
                <div class="no-data-text">
                  <h3 class="no-data-title">No Flows Found</h3>
                  <p class="no-data-subtitle">There are currently no flows available. Create your first flow to get started.</p>
                </div>
              </div>
            </div>
          </template>
          <template v-slot:item="{ item }">
            <tr>
              <td class="text-center">{{ item.number }}</td>
              <td>{{ item.name }}</td>
              <td>
                <ul v-if="item.modelNames" class="model-list">
                  <li v-for="(model, idx) in item.modelNames.split(',')" :key="idx">{{ model.trim() }}</li>
                </ul>
                <span v-else class="text-grey">-</span>
              </td>
              <td class="text-center">{{ item.createBy }}</td>
              <td class="text-center">{{ formatDateTime(item.createDate) }}</td>
              <td class="text-center">
                <div class="action-group">
                  <v-btn
                    id="button-view-flowlist"
                    variant="text"
                    density="comfortable"
                    @click="viewFlow(item)"
                    class="action-btn view-btn"
                    icon
                  >
                    <v-icon size="18">mdi-eye-outline</v-icon>
                    <v-tooltip activator="parent" location="top">
                      View Flow
                    </v-tooltip>
                  </v-btn>

                  <v-btn
                    v-if="canEdit"
                    id="button-edit-flowlist"
                    variant="text"
                    density="comfortable"
                    @click="editFlow(item)"
                    class="action-btn edit-btn"
                    icon
                  >
                    <v-icon size="18">mdi-pencil-outline</v-icon>
                    <v-tooltip activator="parent" location="top">
                      Edit Flow
                    </v-tooltip>
                  </v-btn>
                  
                  <v-btn
                    id="button-export-flowlist"
                    variant="text"
                    density="comfortable"
                    @click="exportFlow(item)"
                    class="action-btn download-btn"
                    icon
                  >
                    <v-icon size="18">mdi-export</v-icon>
                    <v-tooltip activator="parent" location="top">
                      Export Flow
                    </v-tooltip>
                  </v-btn>

                  <v-btn
                    id="button-history-flowlist"
                    variant="text"
                    density="comfortable"
                    @click="openVersionHistory(item)"
                    class="action-btn history-btn"
                    icon
                  >
                    <v-icon size="18">mdi-history</v-icon>
                    <v-tooltip activator="parent" location="top">
                      Version History
                    </v-tooltip>
                  </v-btn>

                  <v-btn
                    id="button-compare-flowlist"
                    variant="text"
                    density="comfortable"
                    @click="toggleFlowForCompare(item)"
                    :class="['action-btn', isSelectedForCompare(item) ? 'compare-btn-active' : 'compare-btn']"
                    icon
                  >
                    <v-icon size="18" :color="isSelectedForCompare(item) ? 'info' : ''">mdi-compare</v-icon>
                    <v-tooltip activator="parent" location="top">
                      {{ isSelectedForCompare(item) ? 'Deselect for Compare' : 'Select for Compare' }}
                    </v-tooltip>
                  </v-btn>

                  <div v-if="canDelete" class="action-divider"></div>

                  <v-btn
                    v-if="canDelete"
                    id="button-delete-flowlist"
                    variant="text"
                    density="comfortable"
                    @click="deleteFlow(item)"
                    class="action-btn delete-btn"
                    icon
                    :disabled="!canDeleteFlow(item)"
                  >
                    <v-icon size="18">mdi-delete-outline</v-icon>
                    <v-tooltip activator="parent" location="top">
                      {{ canDeleteFlow(item) ? 'Delete Flow' : 'You can only delete flows you created' }}
                    </v-tooltip>
                  </v-btn>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table-server>
      </v-card>

      <!-- Floating Compare Bar -->
      <v-slide-y-reverse-transition>
        <v-card v-if="selectedFlowsForCompare.length > 0" class="compare-floating-bar" elevation="8">
          <div class="d-flex align-center pa-3 ga-2">
            <v-icon color="info" class="mr-1">mdi-compare</v-icon>
            <span class="text-body-2 font-weight-medium">Compare:</span>
            <v-chip v-for="f in selectedFlowsForCompare" :key="f.id" size="small" closable
              @click:close="toggleFlowForCompare(f)" color="info" variant="tonal">
              {{ f.name }}
            </v-chip>
            <v-spacer />
            <v-btn v-if="selectedFlowsForCompare.length === 2" color="info" variant="flat" size="small"
              @click="compareSelectedFlows" prepend-icon="mdi-compare">
              Compare Now
            </v-btn>
            <span v-else class="text-caption text-grey">Select {{ 2 - selectedFlowsForCompare.length }} more</span>
            <v-btn icon variant="text" size="x-small" @click="selectedFlowsForCompare = []">
              <v-icon size="16">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card>
      </v-slide-y-reverse-transition>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px" persistent>
        <v-card class="delete-dialog">
            <v-card-title class="delete-dialog-header">
                <div class="header-content">
                    <div class="warning-icon-container">
                        <v-icon size="32" color="white">mdi-alert-circle</v-icon>
                    </div>
                    <div class="header-text">
                        <h3 class="dialog-title">Confirm Delete</h3>
                        <p class="dialog-subtitle">This action cannot be undone</p>
                    </div>
                </div>
            </v-card-title>

            <v-card-text class="delete-dialog-content">
                <div class="confirmation-content">
                    <!-- Flow Info Display -->
                    <div class="flow-info-display">
                        <v-icon class="mr-3" color="primary" size="40">mdi-chart-timeline-variant-shimmer</v-icon>
                        <div class="flow-details">
                            <div class="flow-name">{{ selectedFlow?.name }}</div>
                            <div class="flow-meta">
                                <span class="meta-item">
                                    <v-icon size="14" color="grey">mdi-account</v-icon>
                                    {{ selectedFlow?.createBy }}
                                </span>
                                <span class="meta-item">
                                    <v-icon size="14" color="grey">mdi-calendar</v-icon>
                                    {{ formatDateTime(selectedFlow?.createDate) }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Warning Message -->
                    <div class="warning-alert">
                        <div class="warning-icon">
                            <v-icon size="35">mdi-alert-rhombus</v-icon>
                        </div>
                        <div class="warning-text">
                            <strong>Warning:</strong> This will permanently delete the flow and all its configurations. Make sure you have exported a backup if needed.
                        </div>
                    </div>

                    <!-- Confirmation Text -->
                    <div class="confirmation-text">
                        Are you sure you want to delete this flow?
                    </div>
                </div>
            </v-card-text>

            <v-card-actions class="delete-dialog-actions">
                <v-spacer></v-spacer>
                <v-btn 
                    @click="deleteDialog = false" 
                    variant="outlined" 
                    class="cancel-btn"
                    size="large"
                >
                    <v-icon class="mr-2">mdi-close</v-icon>
                    Cancel
                </v-btn>
                <v-btn
                    v-if="canDelete"
                    @click="confirmDelete"
                    color="error"
                    variant="elevated"
                    class="delete-btn-dialog"
                    size="large"
                >
                    <v-icon class="mr-2">mdi-delete</v-icon>
                    Delete Flow
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      timeout="3000"
      top
    >
      {{ snackbarMessage }}
      <v-btn text @click="snackbar = false">
        Close
      </v-btn>
    </v-snackbar>
    
    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" max-width="500" persistent>
      <v-card class="success-dialog">
        <v-card-title class="success-dialog-header">
          <div class="success-header-content">
            <v-icon class="success-icon" size="40">mdi-check-circle</v-icon>
            <span class="success-title">Deleted Successfully!</span>
          </div>
        </v-card-title>
        
        <v-card-text class="success-dialog-content">
          <div class="success-message">
            {{ successMessage }}
          </div>
          
          <div class="success-details">
            <div class="success-detail-item">
              <v-icon size="18" class="mr-2">mdi-chart-timeline-variant</v-icon>
              <span>Flow: {{ deletedFlowName }}</span>
            </div>
            <div class="success-detail-item">
              <v-icon size="18" class="mr-2">mdi-clock-outline</v-icon>
              <span>{{ formatDateTime(new Date()) }}</span>
            </div>
            <div class="success-detail-item">
              <v-icon size="18" class="mr-2">mdi-delete-circle</v-icon>
              <span>Operation: Delete</span>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="success-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn 
            @click="successDialog = false" 
            color="success" 
            variant="elevated"
            class="success-ok-btn"
          >
            <v-icon class="mr-2">mdi-check</v-icon>
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Version History Dialog -->
    <v-dialog v-model="versionHistoryDialog" max-width="900px" persistent scrollable>
      <v-card class="version-history-dialog">
        <v-card-title class="dialog-header">
          <div class="header-content">
            <v-icon class="mr-3" color="white" size="28">mdi-history</v-icon>
            <div class="header-text">
              <h3 class="dialog-title">Version History</h3>
              <p class="dialog-subtitle">{{ selectedFlow?.name }}</p>
            </div>
          </div>
          <v-btn
            icon
            variant="text"
            @click="closeVersionHistory"
            color="white"
            size="small"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="version-history-content pa-0">
          <v-data-table
            :headers="versionHeaders"
            :items="versions"
            :loading="loadingVersions"
            density="comfortable"
            class="version-table"
            :items-per-page="10"
            show-select
            v-model="selectedVersionsForCompare"
            item-value="id"
            :select-strategy="'page'"
          >
            <template v-slot:item.version="{ item }">
              <div class="d-flex align-center">
                <v-chip
                  size="small"
                  :color="item.activeFlag ? 'success' : 'default'"
                  variant="flat"
                  class="version-chip"
                >
                  v{{ item.version }}
                </v-chip>
                <v-chip
                  v-if="item.activeFlag"
                  size="x-small"
                  color="success"
                  variant="tonal"
                  class="ml-2"
                >
                  <v-icon size="12" class="mr-1">mdi-check-circle</v-icon>
                  Current
                </v-chip>
              </div>
            </template>

            <template v-slot:item.versionComment="{ item }">
              <div class="version-comment-cell">
                {{ item.versionComment || '-' }}
                <v-tooltip 
                  v-if="item.versionComment && item.versionComment.length > 40" 
                  activator="parent" 
                  location="top"
                  max-width="300"
                >
                  {{ item.versionComment }}
                </v-tooltip>
              </div>
            </template>

            <template v-slot:item.createDate="{ item }">
              {{ formatDateTime(item.createDate) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="canEdit"
                variant="text"
                density="comfortable"
                @click="restoreVersion(item)"
                :disabled="item.activeFlag || !canEditFlow(selectedFlow)"
                class="action-btn restore-btn"
                icon
                size="small"
              >
                <v-icon size="16">mdi-restore</v-icon>
                <v-tooltip activator="parent" location="top">
                  {{ item.activeFlag ? 'Current version' : canEditFlow(selectedFlow) ? 'Restore this version' : 'No permission to restore' }}
                </v-tooltip>
              </v-btn>
            </template>

            <template v-slot:no-data>
              <div class="no-data-container">
                <div class="no-data-content">
                  <v-icon size="64" color="grey-lighten-1" class="no-data-icon">
                    mdi-history
                  </v-icon>
                  <div class="no-data-text">
                    <p class="no-data-title">No Version History</p>
                    <p class="no-data-subtitle">This flow has no version history available</p>
                  </div>
                </div>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn
            v-if="selectedVersionsForCompare.length === 2"
            color="info"
            variant="flat"
            @click="compareSelectedVersions"
          >
            <v-icon class="mr-2">mdi-compare</v-icon>
            Compare Selected ({{ selectedVersionsForCompare.length }})
          </v-btn>
          <v-chip v-else-if="selectedVersionsForCompare.length > 0 && selectedVersionsForCompare.length !== 2" size="small" color="warning" variant="tonal">
            Select exactly 2 versions to compare
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn
            @click="closeVersionHistory"
            variant="outlined"
            color="primary"
          >
            <v-icon class="mr-2">mdi-close</v-icon>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Restore Confirmation Dialog -->
    <v-dialog v-model="restoreDialog" max-width="600px" persistent>
      <v-card class="restore-dialog">
        <v-card-title class="dialog-header">
          <div class="header-content">
            <v-icon class="mr-3" color="white" size="28">mdi-restore</v-icon>
            <div class="header-text">
              <h3 class="dialog-title">Restore Version</h3>
              <p class="dialog-subtitle">This will create a new version from the selected one</p>
            </div>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content pa-6">
          <div class="restore-info mb-4">
            <v-alert type="info" variant="tonal" density="compact">
              <div class="text-caption">
                Restoring <strong>version {{ selectedVersionToRestore?.version }}</strong> will create a new version based on its configuration.
              </div>
            </v-alert>
          </div>

          <v-textarea
            v-model="restoreVersionComment"
            label="Version Note (Optional)"
            placeholder="Describe why you're restoring this version..."
            variant="outlined"
            density="comfortable"
            rows="3"
            hide-details="auto"
            color="primary"
            maxlength="500"
            counter="500"
          >
            <template v-slot:prepend-inner>
              <v-icon color="grey-darken-1" size="small">mdi-note-text-outline</v-icon>
            </template>
          </v-textarea>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            @click="closeRestoreDialog"
            variant="outlined"
            :disabled="restoringVersion"
          >
            Cancel
          </v-btn>
          <v-btn
            v-if="canEdit"
            @click="confirmRestore"
            :loading="restoringVersion"
            color="primary"
            variant="elevated"
          >
            <v-icon class="mr-2">mdi-restore</v-icon>
            Restore Version
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Dialog (copied from IntentSessionDetail.vue) -->
    <v-overlay :model-value="dialog" class="align-center justify-center" persistent>
      <v-card class="loading-card" elevation="8">
        <v-card-text class="text-center">
          <v-progress-circular
            indeterminate
            size="64"
            color="primary"
            class="mb-4"
          ></v-progress-circular>
          <h3 class="loading-title">Loading...</h3>
          <p class="loading-subtitle">Please wait</p>
        </v-card-text>
      </v-card>
    </v-overlay>
  </div>
</template>

<script>
import Cookies from "js-cookie";

export default {
  name: "FlowList",
  computed: {
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('FlowList');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('FlowList'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('FlowList'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('FlowList'));
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('FlowList'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('FlowList'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('FlowList'),
      }
    },
    currentUser() {
      return this.$store.state.username;
    },
    userRoleId() {
      return this.roleId;
    }
  },
  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString(),
      searchFlowName: '',
      flows: [],
      loading: false,
      deleteDialog: false,
      selectedFlow: null,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      role:Cookies.get("roleId") == "0"
          ? "Super admin"
          : Cookies.get("roleId") == "1"
            ? "Admin"
            : Cookies.get("roleId") == "2"
              ? "User"
              : "Operator",
      roleId: Cookies.get("roleId"),
      
      // Add success dialog properties
      successDialog: false,
      successMessage: '',
      deletedFlowName: '',

      // Version history properties
      versionHistoryDialog: false,
      versions: [],
      loadingVersions: false,
      versionHeaders: [
        { title: 'Version', key: 'version', align: 'center', sortable: true, width: '120px' },
        { title: 'Comment', key: 'versionComment', align: 'start', sortable: false },
        { title: 'Date', key: 'createDate', align: 'center', sortable: true, width: '180px' },
        { title: 'Created By', key: 'createBy', align: 'center', sortable: false, width: '150px' },
        { title: 'Actions', key: 'actions', align: 'center', sortable: false, width: '100px' }
      ],
      
      // Restore version properties
      restoreDialog: false,
      selectedVersionToRestore: null,
      restoreVersionComment: '',
      restoringVersion: false,

      // Compare version selection
      selectedVersionsForCompare: [],

      // Compare flow selection (main table)
      selectedFlowsForCompare: [],

      // Loading dialog
      dialog: false,

      // Table configuration
      options: {},
      itemsPerPage: 10,
      searchTotalTable: 0,
      headers: [
        { title: 'No.', key: 'number', align: 'center', sortable: false, width: '80px' },
        { title: 'Name', key: 'name', align: 'start', sortable: true },
        { title: 'Models', key: 'modelNames', align: 'start', sortable: false },
        { title: 'Created By', key: 'createBy', align: 'center', sortable: true },
        { title: 'Created Date', key: 'createDate', align: 'center', sortable: true },
        { title: 'Actions', key: 'actions', align: 'center', sortable: false, width: '200px' }
      ],
    }
  },
  watch: {
    options: {
      handler() {
        this.loadFlows()
      },
      deep: true
    }
  },
  mounted() {
    this.loadFlows()
  },
  methods: {
    canDeleteFlow(flow) {
      const isCreator = this.currentUser === flow.createBy;
      const isSuperAdmin = this.userRoleId === '0' || this.userRoleId === '1';
      
      return isCreator || isSuperAdmin;
    },

    async loadFlows() {
      this.loading = true
      try {
        const { page, itemsPerPage, sortBy } = this.options
        const requestData = {
          page: page || 1,
          perPage: itemsPerPage || 10,
          name: this.searchFlowName || null,
          sortBy: sortBy?.[0]?.key || null,
          sortDesc: sortBy?.[0]?.order === 'desc' || false
        }

        this.$call.GetTableFlowManagement(requestData, 
          (response) => {
            if (response.status === 0) {
              this.flows = response.data.data
              this.searchTotalTable = response.data.total
            } else {
              this.showError('Failed to load flows')
            }
            this.loading = false
          },
          (error) => {
            console.error('Error loading flows:', error)
            this.showError('Failed to load flows')
            this.loading = false
          }
        )
      } catch (error) {
        console.error('Error loading flows:', error)
        this.showError('Failed to load flows')
        this.loading = false
      }
    },

    Search() {
      this.options = { ...this.options, page: 1 }
      this.loadFlows()
    },

    createFlow() {
      this.$router.push('/FlowCreator')
    },

    viewFlow(flow) {
      this.$router.push(`/FlowCreator?id=${flow.id}&mode=view`)
    },

    editFlow(flow) {
      this.$router.push(`/FlowCreator?id=${flow.id}&mode=edit`)
    },

    async exportFlow(flow) {
      this.$call.GetFlowById(flow.id,
        (response) => {
          if (response.status === 0) {
            const flowData = response.data
            
            // Convert to flat structure format like Flow.vue
            const flatNodes = this.convertToFlatStructure(flowData.flowDetails || [])
            
            const exportData = {
              name: flowData.name,
              nodes: flatNodes,
              metadata: {
                exportedAt: new Date().toISOString(),
                nodeCount: flatNodes.length,
                originalId: flowData.id
              }
            }

            const dataStr = JSON.stringify(exportData, null, 2)
            const dataBlob = new Blob([dataStr], { type: 'application/json' })
            
            const link = document.createElement('a')
            link.href = URL.createObjectURL(dataBlob)
            link.download = `Flow_Config_${flow.name.replace(/\s+/g, '_')}.json`
            link.click()
            
            this.showSuccess(`Flow "${flow.name}" exported successfully`)
          } else {
            this.showError('Failed to get flow data for export')
          }
        },
        (error) => {
          console.error('Export error:', error)
          this.showError('Failed to export flow')
        }
      )
    },

    deleteFlow(flow) {
      // Double check permission before showing dialog
      if (!this.canDeleteFlow(flow)) {
        this.showError('You can only delete flows that you created');
        return;
      }
      
      this.selectedFlow = flow
      this.deleteDialog = true
    },

    async confirmDelete() {
      // Final permission check before deletion
      if (!this.canDeleteFlow(this.selectedFlow)) {
        this.showError('You are not authorized to delete this flow');
        this.deleteDialog = false;
        this.selectedFlow = null;
        return;
      }

      this.$call.DeleteFlow(this.selectedFlow.id,
        (response) => {
          if (response.status === 0) {
            this.deletedFlowName = this.selectedFlow.name;
            this.showSuccessDialog(`Flow "${this.selectedFlow.name}" deleted successfully`);
            this.loadFlows() // Reload the list
          } else {
            this.showError('Failed to delete flow')
          }
          this.deleteDialog = false
          this.selectedFlow = null
        },
        (error) => {
          console.error('Delete error:', error)
          this.showError('Failed to delete flow')
          this.deleteDialog = false
          this.selectedFlow = null
        }
      )
    },
    showSuccessDialog(message) {
      this.successMessage = message;
      this.successDialog = true;
      
      // Auto close after 3 seconds
      setTimeout(() => {
        this.successDialog = false;
      }, 3000);
    },

    // Convert flowDetails to flat structure format (like Flow.vue)
    convertToFlatStructure(flowDetails) {
      if (!flowDetails || !Array.isArray(flowDetails)) {
        return []
      }

      // Parse flowDetails if they're strings
      const parsedFlowDetails = flowDetails.map(item => {
        if (typeof item === 'string') {
          try {
            return JSON.parse(item)
          } catch (e) {
            return item
          }
        }
        return item
      })

      // Create flat nodes with targets structure
      return parsedFlowDetails.map(node => {
        // Parse data and position if they're strings
        let data = node.data
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data)
          } catch (e) {
            data = { label: node.nodeType || 'Unknown' }
          }
        }

        let position = node.position
        if (typeof position === 'string') {
          try {
            position = JSON.parse(position)
          } catch (e) {
            position = { x: 0, y: 0 }
          }
        }

        let targets = node.targets
        if (typeof targets === 'string') {
          try {
            targets = JSON.parse(targets)
          } catch (e) {
            targets = []
          }
        }

        return {
          id: node.id,
          nodeType: node.nodeType,
          position: position,
          data: data,
          targets: targets || []
        }
      })
    },

    formatDateTime(dateString) {
      if (!dateString) return '-'
      
      try {
        // Handle different date formats from backend
        let date
        if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
          // Add 'Z' to indicate UTC time
          date = new Date(dateString + 'Z')
        } else {
          date = new Date(dateString)
        }
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return dateString // Return original if parsing fails
        }
        
        // Format as dd/MM/yyyy HH:mm:ss in local time
        // JavaScript automatically converts UTC to local timezone
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const seconds = String(date.getSeconds()).padStart(2, '0')
        
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
      } catch (error) {
        console.error('Date formatting error:', error)
        return dateString // Return original if error occurs
      }
    },

    showSuccess(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'success'
      this.snackbar = true
    },

    showError(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'error'
      this.snackbar = true
    },

    showInfo(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'info'
      this.snackbar = true
    },

    // Version History Methods
    canEditFlow(flow) {
      const isCreator = this.currentUser === flow.createBy;
      const isSuperAdmin = this.userRoleId === '0' || this.userRoleId === '1';
      
      return isCreator || isSuperAdmin;
    },

    async openVersionHistory(flow) {
      this.selectedFlow = flow;
      this.versionHistoryDialog = true;
      this.loadingVersions = true;
      
      try {
        this.$call.GetFlowVersions(flow.flowGroupID,
          (response) => {
            if (response.status === 0) {
              this.versions = response.data || [];
            } else {
              this.showError('Failed to load version history');
              this.versions = [];
            }
            this.loadingVersions = false;
          },
          (error) => {
            console.error('Error loading versions:', error);
            this.showError('Failed to load version history');
            this.loadingVersions = false;
          }
        );
      } catch (error) {
        console.error('Error opening version history:', error);
        this.showError('Failed to load version history');
        this.loadingVersions = false;
      }
    },

    closeVersionHistory() {
      this.versionHistoryDialog = false;
      this.versions = [];
      this.selectedFlow = null;
      this.selectedVersionsForCompare = [];
    },

    compareSelectedVersions() {
      if (this.selectedVersionsForCompare.length !== 2) return;
      const leftId = this.selectedVersionsForCompare[0];
      const rightId = this.selectedVersionsForCompare[1];
      const groupId = this.selectedFlow?.flowGroupID || this.selectedFlow?.flowGroupId;
      this.$router.push({
        name: 'FlowCompare',
        query: {
          leftId,
          rightId,
          leftGroupId: groupId,
          rightGroupId: groupId,
        },
      });
    },

    compareSelectedFlows() {
      if (this.selectedFlowsForCompare.length !== 2) return;
      const left = this.selectedFlowsForCompare[0];
      const right = this.selectedFlowsForCompare[1];
      this.$router.push({
        name: 'FlowCompare',
        query: {
          leftId: left.id,
          rightId: right.id,
          leftGroupId: left.flowGroupID || left.flowGroupId,
          rightGroupId: right.flowGroupID || right.flowGroupId,
        },
      });
    },

    toggleFlowForCompare(item) {
      const idx = this.selectedFlowsForCompare.findIndex(f => f.id === item.id);
      if (idx >= 0) {
        this.selectedFlowsForCompare.splice(idx, 1);
      } else if (this.selectedFlowsForCompare.length < 2) {
        this.selectedFlowsForCompare.push(item);
      }
    },

    isSelectedForCompare(item) {
      return this.selectedFlowsForCompare.some(f => f.id === item.id);
    },

    restoreVersion(version) {
      this.selectedVersionToRestore = version;
      this.restoreDialog = true;
      this.restoreVersionComment = `Restored from version ${version.version}`;
    },

    closeRestoreDialog() {
      this.restoreDialog = false;
      this.selectedVersionToRestore = null;
      this.restoreVersionComment = '';
    },

    async confirmRestore() {
      this.restoringVersion = true;
      this.dialog = true;

      try {
        const request = {
          flowID: this.selectedVersionToRestore.id,
          versionComment: this.restoreVersionComment || `Restored from version ${this.selectedVersionToRestore.version}`
        };

        this.$call.RestoreFlowVersion(request,
          (response) => {
            this.dialog = false;
            
            if (response.status === 0) {
              this.showSuccess(`Version ${this.selectedVersionToRestore.version} restored successfully`);
              this.closeRestoreDialog();
              this.closeVersionHistory();
              this.loadFlows(); // Reload flow list
            } else {
              // Handle specific error messages
              if (response.message && response.message.includes('not found')) {
                this.showError('This version has been deleted and cannot be restored');
              } else if (response.message && response.message.includes('permission')) {
                this.showError('You don\'t have permission to restore this version');
              } else {
                this.showError(response.message || 'Failed to restore version');
              }
            }
            this.restoringVersion = false;
          },
          (error) => {
            this.dialog = false;
            console.error('Error restoring version:', error);
            this.showError('Failed to restore version: ' + (error.message || 'Unknown error'));
            this.restoringVersion = false;
          }
        );
      } catch (error) {
        this.dialog = false;
        console.error('Error restoring version:', error);
        this.showError('Failed to restore version');
        this.restoringVersion = false;
      }
    }
  }
}
</script>

<style scoped>
.text-center {
  text-align: center;
}

.card2 {
  border-radius: 16px !important;
  margin-bottom: 2rem;
  margin-top: 1rem;
}

.header-title {
  border-radius: 16px !important;
  margin-bottom: 1rem;
}

.search-panel {
  border-radius: 12px !important;
  background: transparent;
}

.action-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.control-btn {
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  min-width: 120px !important;
  height: 48px !important;
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.control-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
}
.create-btn {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
  color: white !important;
}

.action-group {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.action-btn {
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
}

.action-btn:hover {
  transform: scale(1.1) !important;
}

.view-btn:hover {
  background: rgba(247, 145, 82, 0.3) !important;
  color: #ec8600 !important;
}

/* Table styling */
:deep(.v-data-table) {
  border-radius: 12px !important;
}

:deep(.v-data-table__wrapper) {
  border-radius: 12px !important;
}

:deep(.v-data-table-header) {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
}

:deep(.v-data-table-header th) {
  font-weight: 600 !important;
  color: #374151 !important;
  border-bottom: 2px solid #e5e7eb !important;
}

:deep(.v-data-table__tbody tr:hover) {
  background: rgba(59, 130, 246, 0.02) !important;
}

/* No Data Template */
.no-data-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  padding: 40px 20px;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 400px;
  gap: 24px;
}

.no-data-icon {
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.no-data-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.no-data-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #424242;
  margin: 0;
  letter-spacing: 0.025em;
}

.no-data-subtitle {
  font-size: 0.9rem;
  color: #757575;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
}

/* Responsive design */
@media (max-width: 768px) {
  .action-controls {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }
  
  .control-btn {
    width: 100% !important;
    min-width: auto !important;
  }
}

/* Disabled action button styles */
.action-btn.delete-btn:disabled {
  cursor: not-allowed !important;
  pointer-events: none !important;
  color: #bdbdbd !important;
  background-color: #ffe3e3 !important;
}

.action-btn:disabled .v-icon {
  color: #c3afaf !important;
}

/* Delete Dialog Styles */
.delete-dialog {
    border-radius: 20px !important;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2), 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.delete-dialog-header {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%) !important;
    color: white !important;
    padding: 24px 28px 20px !important;
    border-radius: 0 !important;
}

.delete-dialog-header .header-content {
    display: flex;
    align-items: center;
    gap: 20px;
}

.warning-icon-container {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(10px);
}

.header-text {
    flex: 1;
}

.dialog-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 4px 0;
    color: white;
}

.dialog-subtitle {
    font-size: 0.9rem;
    opacity: 0.9;
    margin: 0;
    color: rgba(255, 255, 255, 0.8);
}

.delete-dialog-content {
    padding: 28px !important;
    background: #fafafa;
}

.confirmation-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.flow-info-display {
    display: flex;
    align-items: center;
    padding: 20px;
    background: white;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.flow-details {
    flex: 1;
}

.flow-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a202c;
    margin-bottom: 8px;
    line-height: 1.2;
}

.flow-meta {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.85rem;
    color: #6b7280;
}

.warning-alert {
  display: flex;
    align-items: center;
    background: rgba(245, 158, 11, 0.1) !important;
    color: #f59e0b !important;
    font-weight: 500 !important;
    margin-top: 16px !important;
    padding: 8px;
    border-radius: 12px !important;
    border: 1px solid rgba(245, 158, 11, 0.3) !important;
}

.warning-icon {
    width: 15%;
}

.warning-text {
    width: 85%;
    font-size: 0.9rem;
    line-height: 1.4;
}

.confirmation-text {
    font-size: 1.1rem;
    font-weight: 500;
    color: #374151;
    text-align: center;
    padding: 12px 0;
}

.delete-dialog-actions {
    padding: 20px 28px 28px !important;
    background: #fafafa;
    gap: 12px;
}

.cancel-btn {
    border-radius: 12px !important;
    border-color: #d1d5db !important;
    color: #6b7280 !important;
    font-weight: 600 !important;
    min-width: 120px !important;
    height: 48px !important;
    transition: all 0.2s ease !important;
}

.cancel-btn:hover {
    border-color: #9ca3af !important;
    background: rgba(156, 163, 175, 0.1) !important;
    transform: translateY(-1px);
}

.delete-btn-dialog {
    border-radius: 12px !important;
    font-weight: 600 !important;
    min-width: 140px !important;
    height: 48px !important;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%) !important;
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3) !important;
    transition: all 0.2s ease !important;
}

.delete-btn-dialog:hover {
    background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%) !important;
    box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4) !important;
    transform: translateY(-2px);
}

.delete-btn-dialog:active {
    transform: translateY(0);
}

/* Responsive design */
@media (max-width: 600px) {
    .delete-dialog-header .header-content {
        flex-direction: column;
        text-align: center;
        gap: 16px;
    }
    
    .flow-info-display {
        flex-direction: column;
        text-align: center;
        gap: 16px;
    }
    
    .flow-meta {
        justify-content: center;
    }
    
    .delete-dialog-actions {
        flex-direction: column;
        gap: 12px;
    }
}

/* Animation for dialog appearance */
.delete-dialog {
    animation: slideInScale 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes slideInScale {
    from {
        opacity: 0;
        transform: scale(0.9) translateY(-20px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}/* Success Dialog Styles */
.success-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
}

.success-dialog-header {
  background: linear-gradient(135deg, #10b943 0%, #047834 100%);
  color: white;
  padding: 24px !important;
  text-align: center;
  border-bottom: none;
}

.success-header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.success-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 12px;
  animation: successPulse 2s infinite;
}

@keyframes successPulse {
  0% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  50% { 
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

.success-title {
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.success-dialog-content {
  padding: 32px 24px !important;
  text-align: center;
  background: #fafafa;
}

.success-message {
  font-size: 1.1rem;
  font-weight: 600;
  color: #047857;
  margin-bottom: 24px;
  line-height: 1.5;
}

.success-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: white;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.1);
}

.success-detail-item {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.success-detail-item .v-icon {
  color: #10b981;
}

.success-dialog-actions {
  padding: 20px 24px !important;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.success-ok-btn {
  height: 44px !important;
  padding: 0 24px !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  background: linear-gradient(135deg, #10b943 0%, #047834 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.success-ok-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4) !important;
}

.success-ok-btn:active {
  transform: translateY(0) !important;
}

/* Animation for dialog entrance */
.success-dialog {
  animation: successSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes successSlideIn {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Version History Dialog Styles */
.version-history-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  max-height: 90vh;
}

.version-history-dialog .dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 20px 24px !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.version-history-dialog .header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.version-history-dialog .header-text {
  flex: 1;
}

.version-history-dialog .dialog-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.version-history-dialog .dialog-subtitle {
  font-size: 0.875rem;
  opacity: 0.9;
  margin: 4px 0 0 0;
  color: rgba(255, 255, 255, 0.85);
}

.version-history-content {
  max-height: 60vh;
  overflow-y: auto;
}

.version-table {
  background: white;
}

.version-table :deep(th) {
  background: #f8fafc !important;
  font-weight: 600 !important;
  color: #374151 !important;
}

.version-chip {
  font-weight: 600 !important;
  font-family: 'Courier New', monospace !important;
}

.version-comment-cell {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.restore-btn {
  color: #3b82f6 !important;
}

.restore-btn:hover {
  background: rgba(59, 130, 246, 0.1) !important;
}

.restore-btn:disabled {
  color: #9ca3af !important;
  cursor: not-allowed !important;
}

.history-btn {
  color: #8b5cf6 !important;
}

.history-btn:hover {
  background: rgba(139, 92, 246, 0.1) !important;
}

/* Restore Dialog Styles */
.restore-dialog {
  border-radius: 20px !important;
  overflow: hidden;
}

.restore-dialog .dialog-header {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  color: white !important;
  padding: 20px 24px !important;
}

.restore-dialog .dialog-content {
  background: #fafafa;
}

.restore-info {
  padding: 16px;
  background: white;
  border-radius: 12px;
}

/* Loading Dialog Styles (from IntentSessionDetail.vue) */
.loading-card {
  border-radius: 16px !important;
  padding: 24px;
  min-width: 300px;
}

.loading-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.loading-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .version-history-dialog {
    max-width: 95% !important;
  }

  .version-comment-cell {
    max-width: 150px;
  }
}

/* Compare floating bar */
.compare-floating-bar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  border-radius: 16px !important;
  min-width: 400px;
  max-width: 700px;
  border: 2px solid rgba(33, 150, 243, 0.2);
}

.compare-btn-active {
  background: rgba(33, 150, 243, 0.1) !important;
}

.model-list {
  margin: 0;
  padding-left: 18px;
  list-style-type: disc;
  font-size: 0.875rem;
  line-height: 1.6;
}

.modern-table :deep(.v-table__wrapper) {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar) {
  width: 6px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-track) {
  background: rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb) {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb:hover) {
  background: rgba(25, 118, 210, 0.5);
}
</style>

